import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Eye, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import { SectionCard, FieldRow, TextAreaRow, GalleryEditor } from "@/components/EntityEditShared";
import { showError } from "@/lib/errors";

export default function FederationEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [fed, setFed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/connexion?next=/federations/${id}/edit`); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/federations/${id}`);
        if (cancelled) return;
        const ok = user.id === data.owner_user_id || (user.roles || []).some((r) => ["admin", "copf"].includes(r));
        if (!ok) { toast.error("Accès réservé au propriétaire."); navigate(`/federations/${data.slug}`); return; }
        setFed(data);
      } catch (e) {
        if (e?.response?.status === 404) navigate("/federations", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, user, authLoading, navigate]);

  const upd = (patch) => setFed((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const fields = {
        name: fed.name, logo_url: fed.logo_url, cover_url: fed.cover_url,
        description: fed.description, short_motto: fed.short_motto, founded_year: fed.founded_year,
        discipline: fed.discipline, president: fed.president, is_copf: fed.is_copf,
        contact_email: fed.contact_email, contact_phone: fed.contact_phone, website: fed.website,
        social_facebook: fed.social_facebook, social_instagram: fed.social_instagram,
        gallery: fed.gallery,
      };
      const { data } = await api.patch(`/federations/${fed.id}`, fields);
      setFed(data);
      toast.success("Fiche fédération sauvegardée.");
    } catch (e) {
      showError(e, { fallback: "Échec de la sauvegarde" });
    } finally { setSaving(false); }
  };

  if (loading || !fed) return <div className="max-w-4xl mx-auto py-8 px-4 space-y-3"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-5" data-testid="federation-edit-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={`/federations/${fed.slug}`}><Button variant="ghost" size="sm" data-testid="fed-edit-back"><ArrowLeft className="h-4 w-4 mr-1" />Retour</Button></Link>
          <h1 className="font-display text-xl sm:text-2xl font-bold truncate flex items-center gap-2"><Crown className="h-5 w-5 text-[hsl(var(--aito-gold))]" />Édition · {fed.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/federations/${fed.slug}`}><Button variant="outline" size="sm" data-testid="fed-edit-preview"><Eye className="h-4 w-4 mr-1.5" />Voir la fiche</Button></Link>
          <Button size="sm" onClick={save} disabled={saving} data-testid="fed-edit-save" className="bg-[hsl(var(--aito-gold))] hover:bg-[hsl(var(--aito-gold)/0.85)] text-black">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      <Tabs defaultValue="identity">
        <TabsList data-testid="fed-edit-tabs">
          <TabsTrigger value="identity" data-testid="fed-edit-tab-identity">Identité</TabsTrigger>
          <TabsTrigger value="contact" data-testid="fed-edit-tab-contact">Contact</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="fed-edit-tab-gallery">Galerie</TabsTrigger>
        </TabsList>

        <TabsContent value="identity" className="mt-4 space-y-4">
          <SectionCard title="Identité" description="Informations principales de la fédération.">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldRow label="Nom" value={fed.name} onChange={(v) => upd({ name: v })} placeholder="Ex. Fédération tahitienne d'athlétisme" testid="fed-field-name" />
              <FieldRow label="Baseline / devise" value={fed.short_motto} onChange={(v) => upd({ short_motto: v })} placeholder="Unir, structurer, valoriser" testid="fed-field-motto" />
              <FieldRow label="Discipline" value={fed.discipline} onChange={(v) => upd({ discipline: v })} placeholder="Ex. surf, va'a, athlétisme…" testid="fed-field-discipline" />
              <FieldRow label="Année de fondation" type="number" value={fed.founded_year || ""} onChange={(v) => upd({ founded_year: v ? parseInt(v, 10) : null })} placeholder="2005" testid="fed-field-founded" />
              <FieldRow label="Président·e" value={fed.president} onChange={(v) => upd({ president: v })} placeholder="Prénom Nom" testid="fed-field-president" />
              <div className="flex items-center gap-2 pt-5">
                <input id="is_copf" type="checkbox" checked={!!fed.is_copf} onChange={(e) => upd({ is_copf: e.target.checked })} data-testid="fed-field-iscopf" className="h-4 w-4" />
                <label htmlFor="is_copf" className="text-sm cursor-pointer">Reconnue COPF</label>
              </div>
              <FieldRow label="Logo (URL)" value={fed.logo_url} onChange={(v) => upd({ logo_url: v })} placeholder="https://...logo.png" testid="fed-field-logo" />
              <FieldRow label="Image de couverture (URL)" value={fed.cover_url} onChange={(v) => upd({ cover_url: v })} placeholder="https://...cover.jpg" testid="fed-field-cover" />
            </div>
          </SectionCard>
          <SectionCard title="À propos" description="Présentation longue de la fédération.">
            <TextAreaRow label="Description" value={fed.description} onChange={(v) => upd({ description: v })} placeholder="Notre fédération regroupe..." rows={6} testid="fed-field-description" />
          </SectionCard>
        </TabsContent>

        <TabsContent value="contact" className="mt-4 space-y-4">
          <SectionCard title="Coordonnées" description="Comment contacter la fédération.">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldRow label="Email" type="email" value={fed.contact_email} onChange={(v) => upd({ contact_email: v })} placeholder="contact@federation.pf" testid="fed-field-email" />
              <FieldRow label="Téléphone" value={fed.contact_phone} onChange={(v) => upd({ contact_phone: v })} placeholder="+689 ..." testid="fed-field-phone" />
              <FieldRow label="Site web" value={fed.website} onChange={(v) => upd({ website: v })} placeholder="https://..." testid="fed-field-website" />
              <FieldRow label="Facebook" value={fed.social_facebook} onChange={(v) => upd({ social_facebook: v })} placeholder="https://facebook.com/..." testid="fed-field-fb" />
              <FieldRow label="Instagram" value={fed.social_instagram} onChange={(v) => upd({ social_instagram: v })} placeholder="https://instagram.com/..." testid="fed-field-ig" />
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="gallery" className="mt-4">
          <SectionCard title="Galerie photos" description="Photos d'événements, AG, podiums.">
            <GalleryEditor items={fed.gallery || []} onChange={(v) => upd({ gallery: v })} />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
